import React, { useCallback, useEffect, useState } from "react";
import { Route as RouteIcon, CheckCircle2, Sparkles } from "lucide-react";
import { tripsApi, apiErrorMessage } from "@/api/client";
import { EmptyState } from "@/components/EmptyState";
import { toast } from "sonner";

const STATE_CLS = {
  proposed: "bg-amber-50 text-amber-700", approved: "bg-emerald-50 text-emerald-700",
  in_progress: "bg-sky-50 text-sky-700", completed: "bg-slate-100 text-slate-500", cancelled: "bg-rose-50 text-rose-600",
  draft: "bg-slate-100 text-slate-500",
};

export default function Trips() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planning, setPlanning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await tripsApi.list(); setItems(data.items); }
    catch (e) { toast.error(apiErrorMessage(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const plan = async () => {
    setPlanning(true);
    const today = new Date().toISOString().slice(0, 10);
    try {
      const { data } = await tripsApi.plan({ days: [today], start: { lat: 51.4545, lng: -2.5879 } });
      toast.success(`Trip planned · ${data.stops.length} stops from ${data.candidate_count} candidates`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setPlanning(false); }
  };

  const approve = async (id) => {
    try { const { data } = await tripsApi.approve(id, { create_visits: true }); toast.success(`Approved · ${data.visits_created} visits created`); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-5 md:py-8" data-testid="trips-page">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">Trips</h1>
          <p className="mt-0.5 text-xs text-slate-400">Deterministic field routes — strong prospects always win the day.</p>
        </div>
        <button data-testid="trip-plan-btn" onClick={plan} disabled={planning}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-teal-700 px-3 text-sm font-semibold text-white hover:bg-teal-800 disabled:opacity-60">
          <Sparkles className={`h-4 w-4 ${planning ? "animate-pulse" : ""}`} /> Plan today
        </button>
      </div>

      {loading ? <div className="h-32 animate-pulse rounded-xl border border-slate-200 bg-white" /> :
        items.length === 0 ? <EmptyState testid="trips-empty-state" icon={RouteIcon} title="No trips yet" description="Plan a trip to build an optimised field route from your real venue locations." /> :
        <div className="space-y-2.5" data-testid="trips-list">
          {items.map((t) => (
            <div key={t.id} data-testid={`trip-${t.id}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <RouteIcon className="h-4 w-4 text-teal-600" />
                  <span className="font-heading text-sm font-bold text-slate-800">{t.dates?.join(", ")}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${STATE_CLS[t.state]}`}>{t.state}</span>
                </div>
                <span className="text-xs text-slate-400">{t.stops?.length || 0} stops</span>
              </div>
              <div className="mt-2 space-y-1" data-testid={`trip-stops-${t.id}`}>
                {(t.stops || []).slice(0, 8).map((s, i) => (
                  <div key={i} className="flex items-start gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs">
                    <span className="mt-0.5 font-bold text-slate-400">{i + 1}</span>
                    <span className="min-w-0 flex-1">
                      <span className="font-semibold text-slate-700">{s.name || s.organisation_id}</span>
                      <span className="block text-[11px] text-slate-400">{(s.reasons || [])[0]} · score {s.score}</span>
                    </span>
                    <span className="rounded bg-teal-100 px-1.5 py-0.5 text-[10px] font-bold text-teal-700">{s.channel}</span>
                  </div>
                ))}
              </div>
              {t.state === "proposed" && (
                <div className="mt-3 flex justify-end">
                  <button data-testid={`trip-approve-${t.id}`} onClick={() => approve(t.id)}
                    className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"><CheckCircle2 className="h-3.5 w-3.5" /> Approve & create visits</button>
                </div>
              )}
            </div>
          ))}
        </div>}
    </div>
  );
}
